"use client";

import { clearTableResponses } from "@/lib/api/table-response-cache";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import Sidebar from "./Sidebar";
import AppHeader from "./AppHeader";
import * as storage from "@/lib/storage";
import { DataProvider } from "@/app/context/DataContext";
import { DEFAULT_COLUMNS, getColLabel, loadSettings, type ColSettings } from "@/lib/columns";
import type { SidebarInitialViews, SidebarView } from "@/lib/sidebar-views";
import { ViewPageSkeleton, type SkelCol, type SkelWidget } from "@/app/view/[id]/ViewPageSkeleton";
import { getColumnResizeMinWidth } from "@/app/view/[id]/columnLayout";
import { ADMIN_PATH, HOME_PATH, LEGACY_HOME_PATH } from "@/lib/app-routes";
import { warmupWorkspacePageNavigation } from "@/lib/api/workspace-page-warmup";

const AUTH_PATHS = ["/login", "/register"];
// Главная и /page/ — страницы новой версии: у них своя шапка и своя боковая панель,
// прежний каркас поверх дал бы два сайдбара и две шапки на одном экране.
const BARE_PREFIXES = ["/shared/", "/frogart", "/legal/", "/landing", "/flow-test", "/home", "/page/", "/product-groups", "/notes", "/settings", "/autopilot-rule-lab", "/whats-new"];
// Новый ключ (v2) — старый хранил строку "collapsed"/"open" без JSON,
// v2 хранит boolean. При первой загрузке после обновления пользователь
// увидит sidebar в дефолтном состоянии один раз, дальше нормально.
const SIDEBAR_KEY = "frogstat_sidebar_v2";
const SIDEBAR_EXPANDED_W = 220;
const SIDEBAR_COLLAPSED_W = 48;
const SIDEBAR_MOTION_MS = 300;

type RoutePendingState = {
  pathname: string;
  title: string;
  kind: "table" | "page" | "route";
  view?: SidebarView | null;
};

const SHELL_FALLBACK_SKELETON_COLUMNS: SkelCol[] = [
  { key: "photo", w: 60, label: "" },
  { key: "offer_id", w: 135, label: "Артикул" },
  { key: "name", w: 260, label: "Название" },
  { key: "orders", w: 90, label: "Заказы" },
  { key: "stock", w: 90, label: "Остаток" },
  { key: "cluster-a", w: 120, label: "" },
  { key: "cluster-b", w: 120, label: "" },
  { key: "cluster-c", w: 120, label: "" },
];

const DEFAULT_SHELL_SKELETON_WIDGETS: SkelWidget[] = [
  { id: 1, c: 0, r: 0, w: 1, h: 1 },
  { id: 2, c: 1, r: 0, w: 1, h: 1 },
  { id: 3, c: 2, r: 0, w: 1, h: 1 },
  { id: 4, c: 3, r: 0, w: 1, h: 1 },
  { id: 5, c: 0, r: 1, w: 2, h: 1 },
  { id: 6, c: 2, r: 1, w: 2, h: 1 },
];

const DEFAULT_COLUMN_WIDTHS = Object.fromEntries(DEFAULT_COLUMNS.map(column => [column.key, column.defaultWidth]));

function buildShellSkeletonColumns(view: SidebarView | null | undefined, settingsMap: Record<string, ColSettings>): SkelCol[] {
  if (!view?.columns?.length) return SHELL_FALLBACK_SKELETON_COLUMNS;
  return view.columns.map(key => {
    const local = settingsMap[key];
    const configuredWidth = view.columnWidths?.[key] ?? local?.width ?? DEFAULT_COLUMN_WIDTHS[key];
    const baseWidth = configuredWidth && configuredWidth !== "auto" ? configuredWidth : 100;
    return {
      key,
      w: Math.max(getColumnResizeMinWidth(key, local?.width), baseWidth),
      label: local?.label ?? getColLabel(key),
    };
  });
}

function getShellSkeletonWidgets(view: SidebarView | null | undefined): SkelWidget[] {
  if (!view || view.widgetsEnabled === false) return [];
  const sourceWidgets = Array.isArray(view.widgets) ? view.widgets : DEFAULT_SHELL_SKELETON_WIDGETS;
  return sourceWidgets
    .filter(widget => (
      Number.isFinite(widget.id) &&
      Number.isFinite(widget.c) &&
      Number.isFinite(widget.r) &&
      Number.isFinite(widget.w) &&
      Number.isFinite(widget.h) &&
      widget.c >= 0 &&
      widget.r >= 0 &&
      widget.w > 0 &&
      widget.h > 0 &&
      widget.c + widget.w <= 4 &&
      widget.r + widget.h <= (view.widgetsRows ?? 2)
    ))
    .map(widget => ({ id: widget.id, c: widget.c, r: widget.r, w: widget.w, h: widget.h }));
}

function shouldShowShellSkeletonSummary(view: SidebarView | null | undefined) {
  return !!view && (view.summaryVariant ?? 0) !== 6;
}

function readInitialSidebarCollapsed() {
  return storage.get<boolean>(SIDEBAR_KEY, false);
}

function applySidebarDocumentState(collapsed: boolean, motionMs = SIDEBAR_MOTION_MS) {
  if (typeof document === "undefined") return;
  const w = collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W;
  document.documentElement.style.setProperty("--sidebar-w", `${w}px`);
  document.documentElement.style.setProperty("--sidebar-motion-ms", `${motionMs}ms`);
  if (collapsed) {
    document.documentElement.dataset.frogstatSidebar = "collapsed";
  } else {
    delete document.documentElement.dataset.frogstatSidebar;
  }
}

function getFastShellNavigation(event: PointerEvent): { pathname: string; href: string; kind: RoutePendingState["kind"] } | null {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const target = event.target instanceof Element ? event.target : null;
  const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
  if (!anchor) return null;
  if (anchor.download) return null;
  const targetAttr = anchor.getAttribute("target");
  if (targetAttr && targetAttr !== "_self") return null;
  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  if (url.pathname.startsWith("/view/")) return null;
  const current = `${window.location.pathname}${window.location.search}`;
  const next = `${url.pathname}${url.search}`;
  if (next === current) return null;
  return {
    pathname: url.pathname,
    href: `${url.pathname}${url.search}${url.hash}`,
    kind: url.pathname.startsWith("/page/") ? "page" : "route",
  };
}

export default function SidebarLayout({
  children,
  initialSidebarViews = null,
}: {
  children: React.ReactNode;
  initialSidebarViews?: SidebarInitialViews;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [collapsed, setCollapsed] = useState(false);
  const [restoredCollapsedFromStorage, setRestoredCollapsedFromStorage] = useState(false);
  const sidebarHydratedRef = useRef(false);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [routePending, setRoutePending] = useState<RoutePendingState | null>(null);
  const [localColSettingsMap] = useState<Record<string, ColSettings>>(() =>
    Object.fromEntries(loadSettings().map(setting => [setting.key, setting]))
  );
  const isAuth = AUTH_PATHS.includes(pathname);
  // /view/<id>/embed — вид без каркаса для встраивания в страницы (iframe)
  const isBare = pathname === HOME_PATH || pathname === ADMIN_PATH || pathname === "/cost" || BARE_PREFIXES.some(p => pathname.startsWith(p)) || /^\/(view|page)\/[^/]+\/embed/.test(pathname);
  const needsAnalyticsData =
    pathname === LEGACY_HOME_PATH ||
    pathname.startsWith("/view/") ||
    pathname.startsWith("/templates/preview/") ||
    pathname.startsWith("/tasks") ||
    pathname.startsWith("/cost-prices") || pathname === "/v1/cost" ||
    pathname.startsWith("/ab-tests") ||
    pathname.startsWith("/assortment-map");

  // Проверяем impersonation один раз при монтировании. Раньше зависело от
  // pathname — и на каждый переход по страницам дёргало /api/auth/me (10+
  // лишних запросов за минуту). Impersonation может измениться только если
  // админ нажал «Выйти» на баннере (тот делает window.location.reload,
  // так что layout перемонтируется и эффект сработает заново).
  useEffect(() => {
    if (isAuth) return;
    fetch("/api/auth/me").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.impersonating) {
        setImpersonating(true);
        setImpersonatedEmail(d.user?.email ?? null);
      } else {
        setImpersonating(false);
      }
      setDemoMode(Boolean(d?.user?.demo));
    }).catch(() => {});
  }, [isAuth]);

  const w = collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W;

  useLayoutEffect(() => {
    if (isAuth) return;
    const storedCollapsed = readInitialSidebarCollapsed();
    sidebarHydratedRef.current = true;
    setCollapsed(storedCollapsed);
    setRestoredCollapsedFromStorage(storedCollapsed);
    applySidebarDocumentState(storedCollapsed, 0);
    window.dispatchEvent(new Event("frogstat:sidebar-resized"));
  }, [isAuth]);

  useEffect(() => {
    if (isAuth || !sidebarHydratedRef.current) return;
    applySidebarDocumentState(collapsed);
    window.dispatchEvent(new Event("frogstat:sidebar-resized"));
    // ВАЖНО: --sidebar-motion-ms нужен ТОЛЬКО на время анимации сайдбара. Если его
    // оставить 300ms навсегда (как было), то постоянный `transition: left` висит на
    // всех закреплённых колонках/шапках таблицы — и любой пересчёт их `left` при
    // горизонтальном скролле (смена «последней липкой», возврат к краю) проигрывается
    // как 300-мс «доезд на место» + лишняя работа стилей на старте движения. Поэтому
    // после завершения анимации возвращаем 0ms → в покое и при скролле липкость
    // мгновенная.
    const resetMotion = window.setTimeout(() => {
      document.documentElement.style.setProperty("--sidebar-motion-ms", "0ms");
    }, SIDEBAR_MOTION_MS + 50);
    return () => window.clearTimeout(resetMotion);
  }, [collapsed, isAuth]);

  useEffect(() => {
    delete document.documentElement.dataset.frogstatPendingPathname;
    const timer = window.setTimeout(() => setRoutePending(null), 0);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    if (!routePending) return;
    const timeoutMs = routePending.kind === "table" ? 45_000 : 2_500;
    const timer = window.setTimeout(() => {
      delete document.documentElement.dataset.frogstatPendingPathname;
      setRoutePending(null);
    }, timeoutMs);
    return () => window.clearTimeout(timer);
  }, [routePending]);

  useEffect(() => {
    if (isAuth || isBare) return;
    const handler = (event: PointerEvent) => {
      const navigation = getFastShellNavigation(event);
      if (!navigation) return;
      event.preventDefault();
      document.documentElement.dataset.frogstatPendingPathname = navigation.pathname;
      if (navigation.kind === "page") void warmupWorkspacePageNavigation(navigation.pathname);
      window.dispatchEvent(new CustomEvent("frogstat:route-pending", {
        detail: {
          pathname: navigation.pathname,
          kind: navigation.kind,
        },
      }));
      router.push(navigation.href);
    };
    document.addEventListener("pointerdown", handler, true);
    return () => document.removeEventListener("pointerdown", handler, true);
  }, [isAuth, isBare, router]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ pathname?: string; title?: string; kind?: RoutePendingState["kind"]; view?: SidebarView | null }>).detail;
      const pendingPathname = detail?.pathname ?? "";
      if (!pendingPathname || pendingPathname === pathname) {
        setRoutePending(null);
        return;
      }
      setRoutePending({
        pathname: pendingPathname,
        title: detail?.title?.trim() || (pendingPathname.startsWith("/page/") ? "Страница" : pendingPathname.startsWith("/view/") ? "Таблица" : "Страница"),
        kind: detail?.kind ?? (pendingPathname.startsWith("/page/") ? "page" : pendingPathname.startsWith("/view/") ? "table" : "route"),
        view: detail?.view ?? null,
      });
    };
    window.addEventListener("frogstat:route-pending", handler);
    return () => window.removeEventListener("frogstat:route-pending", handler);
  }, [pathname]);

  if (isAuth || isBare) return (
    <>
      {children}
    </>
  );

  const toggle = () => {
    // Side-effect'ы НЕ внутри апдейтера setState (апдейтер выполняется во время
    // рендера → dispatchEvent там дёргал setState в других компонентах =
    // «Cannot update a component while rendering»). applySidebarDocumentState
    // зовём синхронно, чтобы не было мигания; событие frogstat:sidebar-resized
    // диспатчит эффект на смену `collapsed` (строки выше).
    const next = !collapsed;
    applySidebarDocumentState(next);
    storage.set(SIDEBAR_KEY, next);
    setCollapsed(next);
  };

  const stopImpersonation = async () => {
    await fetch("/api/admin/impersonate/stop", { method: "POST" });
    await clearTableResponses();
    localStorage.clear();
    try {
      const dbs = await indexedDB.databases?.();
      if (dbs) for (const db of dbs) if (db.name) indexedDB.deleteDatabase(db.name);
    } catch {}
    // Полная перезагрузка вместо router.push/refresh — Next.js router обновляет
    // только server components, а React Context (RefreshContext, session в
    // памяти) остаётся от прошлого юзера и UI глючит. window.location заставляет
    // перечитать всё с нуля.
    window.location.href = ADMIN_PATH;
  };

  const shell = (
    <div id="app-root" className="min-h-screen">
      <Sidebar
        collapsed={collapsed}
        onToggle={toggle}
        initialViews={initialSidebarViews}
        restoreCollapsedImmediately={restoredCollapsedFromStorage}
      />
      <div
        data-frogstat-sidebar-content
        style={{
          marginLeft: w,
          flex: 1,
          minWidth: 0,
          transition: "margin-left var(--sidebar-motion-ms, 300ms) cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        >
          <AppHeader />
          {children}
          {routePending && routePending.pathname !== pathname && routePending.kind === "table" && (
            <div
              className="fixed bottom-0 right-0 z-[28] flex items-start overflow-y-auto"
              style={{ left: "var(--sidebar-w, 220px)", top: "var(--topbar-h, 76px)", background: "var(--background)" }}
              aria-live="polite"
              aria-busy="true"
            >
              <div className="w-full px-5 pb-8 pt-5">
                <ViewPageSkeleton
                  skelGroups={null}
                  skelCols={buildShellSkeletonColumns(routePending.view, localColSettingsMap)}
                  columnGroups={routePending.view?.colGroups}
                  readOnly={false}
                  compact={!!routePending.view?.compact}
                  showSummary={shouldShowShellSkeletonSummary(routePending.view)}
                  widgets={getShellSkeletonWidgets(routePending.view)}
                  widgetsRows={routePending.view?.widgetsRows === 1 ? 1 : 2}
                  embedded
                />
              </div>
            </div>
          )}
        </div>
      {impersonating && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white text-xs px-4 py-2.5 rounded-xl shadow-lg border border-gray-700">
          <span className="text-yellow-400">👤</span>
          <span>Вы вошли как <strong>{impersonatedEmail ?? "пользователь"}</strong></span>
          <button
            onClick={stopImpersonation}
            className="ml-2 px-3 py-1 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Вернуться в админа
          </button>
        </div>
      )}
      {demoMode && !impersonating && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white text-xs px-4 py-2.5 rounded-xl shadow-lg border border-gray-700">
          <span>🐸</span>
          <span>Демо-режим: данные вымышленные, изменения отключены</span>
          <a
            href="/register"
            className="ml-2 px-3 py-1 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors whitespace-nowrap"
          >
            Создать свой аккаунт
          </a>
          <button
            type="button"
            onClick={async () => {
              await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
              await clearTableResponses();
              window.location.assign("/");
            }}
            className="px-3 py-1 rounded-lg font-medium border border-white/25 text-white/80 hover:bg-white/10 transition-colors whitespace-nowrap"
          >
            Выйти из демо
          </button>
        </div>
      )}
    </div>
  );
  return needsAnalyticsData ? <DataProvider>{shell}</DataProvider> : shell;
}
